import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {getErrorMessage} from '@guestapp/core';
import {
  useBuildTotalPayments,
  usePath,
  usePayment,
  usePaymentProcessingState,
  usePaymentSettings,
} from '../../hooks';
import {useAddonPayments} from 'hooks/payments/useAddonPayments';
import {useFetchAddonMultiPayment} from 'components/AddonPaymentForm/useFetchAddonMultiPayment';
import {StatusModalStates} from 'components/StatusModal';
import {MultiPayment, PAYMENT_PROVIDERS, PaymentsCart} from '@guestapp/sdk';
import {PATHS} from 'Routes';
import {ProviderForm} from './ProviderForm';
import {PaymentProcessingModal} from '../PaymentProcessingModal';
import {Container, Price, TotalBox} from './styled';

type Props = {
  isLoading: boolean;
  onSuccess: () => void;
  multiPayment?: MultiPayment;
  paymentsCart?: PaymentsCart;
};
function AddonPaymentForm({paymentsCart, multiPayment, isLoading, onSuccess}: Props) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const [isPaymentFormLoading, setIsPaymentFormLoading] = useState(true);
  const {
    processState,
    error,
    onPaymentSuccess,
    onPaymentError,
    setProcessState,
    closeProcessingModal,
  } = usePaymentProcessingState({
    onSuccess,
  });

  const isDisabled = processState !== StatusModalStates.IDLE;

  const {paymentProvider, currencySymbol} = usePaymentSettings();
  const {totalToPayNow} = useBuildTotalPayments({paymentsCart});

  const isAddonProvider = paymentProvider === PAYMENT_PROVIDERS.addon;

  const {data: hppData, isLoading: isHppDataLoading} = useFetchAddonMultiPayment({
    id: multiPayment?.id,
    enabled: !isDisabled,
  });

  const {resumeListening} = usePayment({
    onPaymentSuccess,
    onPaymentError,
  });

  const handleAddonPaymentSubmitted = useCallback(() => {
    if (!multiPayment?.id) {
      return;
    }
    setProcessState(StatusModalStates.LOADING);

    resumeListening(multiPayment.id);
  }, [multiPayment?.id, resumeListening, setProcessState]);

  useAddonPayments({
    hppData,
    onInit: () => {
      setProcessState(StatusModalStates.LOADING);
    },
    onError: error => {
      console.error('Global Payments initialization error:', error);
      const errorMsg = getErrorMessage(error ?? 'Something went wrong');
      onPaymentError(errorMsg, errorMsg);
    },
    onLoad: () => setIsPaymentFormLoading(false),
    onSuccess: handleAddonPaymentSubmitted,
    enabled: isAddonProvider,
  });

  useEffect(() => {
    if (isAddonProvider && !isHppDataLoading && !hppData) {
      navigate(getFullPath(PATHS.payments.main));
    }
  }, [isAddonProvider, isHppDataLoading, hppData, navigate, getFullPath]);

  return (
    <Container className="payment-form relative">
      <ProviderForm isLoading={isPaymentFormLoading} />
      <TotalBox className="payment-form__total-box">
        <span>{t('total_payment')}</span>
        <Price $isLoaded={!isLoading} className="payment-form__price">
          {totalToPayNow ?? 0}
          {currencySymbol}
        </Price>
      </TotalBox>
      <PaymentProcessingModal
        isOpen={processState !== StatusModalStates.IDLE}
        close={() => {
          if (error) {
            navigate(getFullPath(PATHS.payments.main));
            return;
          }
          closeProcessingModal();
        }}
        state={processState}
        error={error}
      />
    </Container>
  );
}

export {AddonPaymentForm};
